import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA } from '@angular/material/dialog'
import {
  AppointmentAuthorizationService,
  AuthorizationHistory,
  AuthorizationHistoryEventType,
  AuthorizationModel,
  FacilityService,
  isISODateString,
} from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import _ from 'lodash'
import moment from 'moment-timezone'
import { BehaviorSubject, filter, finalize, map, Observable, shareReplay, Subject, switchMap, takeUntil, tap } from 'rxjs'

@Component({
  selector: 'mmx-auth-history',
  templateUrl: './patient-auth-history-dialog.component.html',
  styleUrls: ['./patient-auth-history-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PatientAuthHistoryDialogComponent implements OnInit, OnDestroy {
  private onDestroy$ = new Subject<void>()

  tz = 'America/New_York'

  readonly calendarFormats = {
    sameDay: '[Today]',
    nextDay: '[Tomorrow]',
    nextWeek: 'dddd',
    lastDay: '[Yesterday]',
    lastWeek: '[Last] dddd',
    sameElse: 'MM/DD/YYYY',
  }

  inProgress = true

  appointments: Record<string, string> = {}

  public authorizationId$ = new BehaviorSubject<string>(null)

  public historiesAndAppointments$ = this.authorizationId$.pipe(
    takeUntil(this.onDestroy$),
    filter(Boolean),
    tap(() => this.inProgress = true),
    switchMap(id => this.authApi.getAuthHistory(id).pipe(
      finalize(() => this.inProgress = false),
    )),
    shareReplay(1),
  )

  public histories$: Observable<{ date: moment.Moment, records: AuthorizationHistory[] }[]> = this.historiesAndAppointments$.pipe(
    map(resp => {
      for (const appointment of resp.appointments) {
        this.appointments[appointment.id] = moment.tz(appointment.startAt, this.tz).format('LL LT')
      }

      return _(resp.histories)
        .orderBy('createdAt', 'desc')
        .groupBy((authHistory) => {
          return moment.tz(authHistory.createdAt, this.tz).format('YYYY-MM-DD')
        })
        .map((records: AuthorizationHistory[], date) => ({ date: moment.tz(date, this.tz), records }))
        .value()
    }),
  )

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: { authorization: AuthorizationModel },
    private authApi: AppointmentAuthorizationService,
    private facilityService: FacilityService,
    private translateService: TranslateService,
  ) {
    this.tz = this.facilityService.currentTimezone()
  }

  ngOnInit(): void {
    this.authorizationId$.next(this.data.authorization.id)
  }

  ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  getEventTypeText(auth: AuthorizationHistory) {
    switch (auth.eventType) {
      case AuthorizationHistoryEventType.AuthCreated:
        return this.translateService.instant('authorization-history.event-text.created')
      case AuthorizationHistoryEventType.AuthEdited:
        return this.translateService.instant('authorization-history.event-text.updated')
      case AuthorizationHistoryEventType.AuthPulled:
        return this.translateService.instant('authorization-history.event-text.pulled')
      case AuthorizationHistoryEventType.AuthSubmitted:
        return this.translateService.instant('authorization-history.event-text.submitted')
    }
  }

  getByText(auth: AuthorizationHistory) {
    if (!auth.clinician?.name) {
      return ''
    }

    return ' ' + this.translateService.instant('common.by') + ` ${auth.clinician.name}`
  }

  getChanges(auth: AuthorizationHistory): { key: string, value: string }[] {
    if (auth.eventType === AuthorizationHistoryEventType.AuthSubmitted) {
      return auth.submittedData ? [{
        key: this.translateService.instant('authorization-history.changes.submitted'),
        value: ` ${JSON.stringify(auth.submittedData)}`,
      }] : []
    }

    if (!auth.change) {
      return []
    }

    const result = []
    for (let key in auth.change) {
      if (['insuranceId'].includes(key)) {
        continue
      }

      const change = auth.change[key]
      let oldValue = change.oldValue
      let newValue = change.newValue

      if (isISODateString(oldValue)) {
        oldValue = moment.tz(oldValue, this.tz).format('LL LT')
      }
      if (isISODateString(newValue)) {
        newValue = moment.tz(newValue, this.tz).format('LL LT')
      }

      // transform appointment ids into dates
      if (['appointmentIds', 'outdatedAppointmentIds', 'ignoredOutdatedIds'].includes(key)) {
        if (oldValue) {
          oldValue = oldValue.map(id => this.appointments[id])
        }

        newValue = newValue.map(id => this.appointments[id])
      }

      if (_.isArray(oldValue)) {
        oldValue = oldValue.join(', ')
      }
      if (_.isArray(newValue)) {
        newValue = newValue.join(', ')
      }

      if (oldValue != null && !_.isString(oldValue)) {
        oldValue = oldValue.toString()
      }
      if (!_.isString(newValue)) {
        newValue = newValue.toString()
      }

      if (key === 'status') {
        if (oldValue) {
          oldValue = this.translateService.instant(`statuses.authorizations.${oldValue}`)
        }
        newValue = this.translateService.instant(`statuses.authorizations.${newValue}`)
      }

      if (key !== 'attachments') {
        const text = oldValue
          ? this.translateService.instant('authorization-history.changes.changed', {
            from: oldValue,
            to: newValue,
          })
          : this.translateService.instant('authorization-history.changes.set', { to: newValue })

        result.push({
          key,
          value: ` ${text}`,
        })
      } else if (newValue) {
        result.push({
          key: this.translateService.instant('authorization-history.changes.attachments'),
          value: ` <b>${newValue}</b>`,
        })
      }
    }

    return result
  }
}
